import "./style.scss";

import type { IChannel } from "containers/Channels/types";
import type { ChannelParams, GroupParams } from "containers/TermFormContainer";
import {
    isMilestoneThresholdAmountBased,
    resolveChannelMilestoneBaseColumn,
} from "containers/TermFormContainer/ChannelsStep/milestoneBaseColumnUtils";
import { formatNumber } from "helpers/utils";
import { useCompanySettings } from "hooks/useCompanySettings";
import { useTranslation } from "react-i18next";
import type { CalculationBaseColumn } from "store/calculationBaseColumnLabelsStore";

interface IGroupRecap {
    /**
     * Array with all channels chosen in this form.
     */
    chosenGroup: GroupParams;
    /**
     * Complete data of the chosen channel (chosen channel only has id and milestones values).
     */
    fullChannelData?: IChannel | undefined;
    /**
     * First contract channel in the synced group (carries milestoneBaseColumn).
     */
    baseChannel?: ChannelParams;
    /**
     * Boolean to know whether channel accepts milestones or not.
     */
    hasMilestones: boolean;
}

/**
 * Similar to the MilestonesInputs component except the information is hard data and cannot be modified.
 */
const GroupMilestonesRecap = ({ chosenGroup, fullChannelData, baseChannel, hasMilestones }: IGroupRecap) => {
    const [t] = useTranslation();
    const { currencySymbol } = useCompanySettings();

    const milestoneBaseColumn: CalculationBaseColumn = fullChannelData
        ? resolveChannelMilestoneBaseColumn(fullChannelData, baseChannel)
        : baseChannel?.milestoneBaseColumn ?? "quantity";
    const isAmountBasedThreshold = isMilestoneThresholdAmountBased(milestoneBaseColumn);

    return (
        <>
            <div className="milestones-recap" id={`milestone-recap-${chosenGroup?.id}`}>
                {chosenGroup.data.map((mile, index) => {
                    const previousIterationMilestone = index > 0 ? +chosenGroup.data[index - 1]?.quantity + 1 : 0;

                    const isLastLine = mile.quantity === Infinity || mile.quantity === "Infinity";
                    const mileQuantity = mile.quantity !== Infinity && mile?.quantity;
                    const milePercentage = mile.percentage === "" ? 0 : mile.percentage;
                    return (
                        <div
                            className="milestone-line"
                            key={`milestone-${index}`}
                            style={{
                                marginBottom: `${hasMilestones && !isLastLine ? "1rem" : isLastLine ? "0.5rem" : ""}`,
                                marginTop: `${index === 0 && "0.25rem"}`,
                            }}
                        >
                            <div className="left-side">
                                <span>{formatNumber(+previousIterationMilestone) ?? 0}</span>
                                <span className="text-grey">
                                    {isLastLine &&
                                        (isAmountBasedThreshold ? currencySymbol : t("MilestonesForm.Sales"))}{" "}
                                    {t("MilestonesForm.To")}
                                </span>
                                {isLastLine ? (
                                    <span>{t("MilestonesForm.InfinityText")}</span>
                                ) : (
                                    <span>
                                        {!hasMilestones
                                            ? t("MilestonesForm.InfinityText")
                                            : formatNumber(+mileQuantity) ?? 0}
                                    </span>
                                )}
                                {!isLastLine && (
                                    <span className="text-grey">
                                        {isAmountBasedThreshold ? currencySymbol : t("MilestonesForm.Sales")}
                                    </span>
                                )}
                            </div>
                            <div className="right-side">
                                <span className="percentage">{milePercentage ?? 0}</span>
                                <span className="text-grey">{t("MilestonesForm.Percentage")}</span>
                            </div>
                        </div>
                    );
                })}
            </div>
        </>
    );
};

export default GroupMilestonesRecap;
